import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Clock, Plus, Gift } from "@phosphor-icons/react";
import { toast } from "sonner";
import { emitServicesChanged } from "@/lib/servicesSync";

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

export default function RemoteMinutesSection({ customerId }) {
  const [balance, setBalance] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: bal }, { data: cat }] = await Promise.all([
        api.get(`/customers/${customerId}/remote-minutes`),
        api.get("/remote-minutes/catalog"),
      ]);
      setBalance(bal);
      setCatalog(cat);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const purchase = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/customers/${customerId}/remote-minutes/purchase`, { package: chosen.key });
      toast.success(`${data.purchase.label} attivato — +${data.purchase.minutes_added} minuti`);
      setPurchaseOpen(false);
      setChosen(null);
      emitServicesChanged(customerId);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const open = () => { setChosen(null); setPurchaseOpen(true); };

  if (loading) return <Card className="p-4 border-border/70 rounded-lg text-sm text-muted-foreground">Caricamento minuti remoti…</Card>;

  const available = balance?.available ?? 0;
  const empty = available === 0;

  return (
    <Card className="p-5 border-border/70 rounded-lg" data-testid="remote-minutes-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold tracking-tight text-lg flex items-center gap-2">
          <Clock size={20} weight="duotone" className="text-primary" /> Minuti Assistenza Remota
        </h2>
        <Button size="sm" onClick={open} data-testid="purchase-remote-minutes-btn" className="rounded-full gap-2">
          <Plus size={14} weight="bold" /> Vendi pacchetto
        </Button>
      </div>

      <div className="rounded-md bg-secondary/40 border border-border/50 p-4 text-center">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Saldo disponibile</div>
        <div className="mt-1">
          <span className={`text-4xl font-display font-bold ${empty ? "text-destructive" : "text-foreground"}`} data-testid="remote-minutes-balance">
            {available}
          </span>
          <span className="text-lg text-muted-foreground ml-1">min</span>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Prepagati · non scadono · non si resettano
        </div>
        {empty && (
          <div className="mt-3 text-xs text-destructive italic">
            Nessun minuto residuo. Vendi un nuovo pacchetto per ricaricare.
          </div>
        )}
      </div>

      <Dialog open={purchaseOpen} onOpenChange={setPurchaseOpen}>
        <DialogContent className="max-w-2xl" data-testid="purchase-remote-dialog">
          <DialogHeader>
            <DialogTitle>Vendi pacchetto Minuti Assistenza Remota</DialogTitle>
            <DialogDescription>
              Prezzi IVA esclusa. I minuti si sommano al saldo esistente.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {catalog.map((pkg) => (
              <button
                key={pkg.key}
                type="button"
                onClick={() => setChosen(pkg)}
                data-testid={`remote-package-${pkg.key}`}
                className={`p-4 rounded-md border text-left ${chosen?.key === pkg.key ? "border-primary bg-primary/10 ring-2 ring-primary/40" : "border-border hover:bg-secondary"}`}
                style={{ transition: "background-color 0.15s ease, border-color 0.15s ease" }}
              >
                <div className="font-display font-bold text-lg">{pkg.label}</div>
                <div className="font-mono text-xs text-muted-foreground mt-1">{eur(pkg.price)} + IVA</div>
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  <Badge variant="secondary" className="font-mono text-xs">+{pkg.minutes} min</Badge>
                  {pkg.bonus_minutes > 0 && (
                    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40 border font-mono text-xs gap-1">
                      <Gift size={11} weight="fill" /> +{pkg.bonus_minutes} omaggio
                    </Badge>
                  )}
                </div>
                <div className="mt-2 font-mono text-[11px] text-foreground/80">
                  Totale: <span className="font-bold">{pkg.total_minutes} min</span>
                </div>
              </button>
            ))}
          </div>
          {chosen && balance && (
            <div className="mt-4 rounded-md bg-secondary/60 border border-border/50 p-3 text-sm">
              <div className="flex justify-between font-mono text-xs">
                <span className="text-muted-foreground">Saldo attuale</span>
                <span>{balance.available} min</span>
              </div>
              <div className="flex justify-between font-mono text-xs mt-0.5">
                <span className="text-muted-foreground">+ Pacchetto {chosen.label}</span>
                <span className="text-emerald-600 dark:text-emerald-400">+{chosen.total_minutes} min</span>
              </div>
              <div className="border-t border-border/50 my-2"></div>
              <div className="flex justify-between font-bold text-sm">
                <span>Nuovo saldo</span>
                <span>{balance.available + chosen.total_minutes} min</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPurchaseOpen(false)}>Annulla</Button>
            <Button onClick={purchase} disabled={!chosen || busy} data-testid="confirm-remote-purchase-btn" className="rounded-full">
              {busy ? "Registro…" : chosen ? `Conferma vendita ${chosen.label}` : "Seleziona un pacchetto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
